import type { LocaleCode } from '../../../../utils/Locale';
import type { SiteContentPath } from '../../modules/siteContent/types/siteContentPaths';
import type { QueryKey } from '../types/QueryKey';

/** Directory name where fallback GraphQL responses are stored, relative to the /public directory. */
export const FALLBACK_RESPONSES_DIR_NAME = 'fallback-graphql-responses';

/**
 * Return fallback response json file path relative to the `fallback-graphql-responses` directory.
 *
 * @param queryKey - The key of the GraphQL query. e.g. 'GET_NAVIGATION_NODE_LISTING_QUERY'.
 * @param locale - Optional locale code for the response, e.g. 'en', 'zh-TW', if the query is localized.
 * @param subPath - Optional sub-path to append to the query key, for 'GET_SITE_CONTENT_ELEMENT_LISTING_QUERY' query, e.g. '/About/InvestorRelations'
 */
export function getFallbackQueryReponsePath(
    queryKey: QueryKey,
    locale?: LocaleCode,
    subPath?: SiteContentPath
): string {
    const segments = ['pimcore', queryKey];

    if (subPath) {
        const clean = subPath.replace(/^\//, '');
        segments.push(...clean.split('/'));
    }

    segments.push('response');

    let filePath = segments.join('/');

    if (locale) {
        filePath += `-${locale}`;
    }

    filePath += '.json';

    return filePath;
}

// add more types to the union as needed
export type SubPath = SiteContentPath;

export async function fetchFallbackQueryResponse(
    queryKey: QueryKey,
    locale?: LocaleCode,
    subPath?: SubPath
): Promise<any> {
    const relativePath = getFallbackQueryReponsePath(queryKey, locale, subPath);
    const url = `${process.env.PUBLIC_URL || ''}/${FALLBACK_RESPONSES_DIR_NAME}/${relativePath}`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(
            `Failed to fetch fallback response for ${queryKey}: ${response.statusText}`
        );
    }

    return response.json();
}
